import { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { ClassData, SignupData } from '../types';

export const useClasses = (db: any, appId: string, user: any) => {
    const [classes, setClasses] = useState<ClassData[]>([]);
    const [signups, setSignups] = useState<SignupData[]>([]);

    useEffect(() => {
        if (!user || !db) return;
        const classesRef = collection(db, 'artifacts', appId, 'public', 'data', 'classes');
        const signupsRef = collection(db, 'artifacts', appId, 'public', 'data', 'signups');
    
        const unsubClasses = onSnapshot(classesRef, (snapshot) => {
          setClasses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ClassData)));
        });
        const unsubSignups = onSnapshot(signupsRef, (snapshot) => {
          setSignups(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as SignupData)));
        });
        
        return () => { unsubClasses(); unsubSignups(); };
      }, [user, db, appId]);

    // Data Indexing
    const signupsByClassId = useMemo(() => {
        const map: Record<string, SignupData[]> = {};
        signups.forEach(s => {
            if (!map[s.classId]) map[s.classId] = [];
            map[s.classId].push(s);
        });
        return map;
    }, [signups]);

    const classesByDate = useMemo(() => {
        const map: Record<string, ClassData[]> = {};
        classes.forEach(c => {
            if (!map[c.date]) map[c.date] = [];
            map[c.date].push(c);
        });
        
        Object.keys(map).forEach(key => {
            map[key].sort((a,b) => a.startTime.localeCompare(b.startTime));
        });
        
        return map;
    }, [classes]);

    const orphanedClasses = useMemo(() => {
        return classes.filter(c => !c.date || c.date === '');
    }, [classes]);

    const nextUpcomingClass = useMemo(() => {
        const now = new Date();
        const getDateTime = (c: ClassData) => {
            const [h, m] = c.startTime.split(':').map(Number);
            const d = new Date(c.date + 'T00:00:00'); 
            d.setHours(h, m, 0, 0);
            return d;
        };
    
        const allFuture = classes.filter(c => {
            const cTime = getDateTime(c);
            return cTime > now;
        });
    
        allFuture.sort((a, b) => {
            if (a.date !== b.date) return a.date.localeCompare(b.date);
            return a.startTime.localeCompare(b.startTime);
        });
    
        return allFuture.length > 0 ? allFuture[0] : null;
    }, [classes]);

    return { classes, signups, signupsByClassId, classesByDate, orphanedClasses, nextUpcomingClass };
};
